import type { ElementType } from 'react';

export interface TabBarItem<T extends string> {
  id:          T;
  label:       string;
  /** Rótulo curto usado abaixo de `sm` (640px). Default: `label`. */
  shortLabel?: string;
  icon?:       ElementType;
  /** Contador exibido à direita do rótulo (ex.: alertas abertos). */
  badge?:      number;
}

interface Props<T extends string> {
  items:    readonly TabBarItem<T>[];
  active:   T;
  onChange: (id: T) => void;
  /** Nome acessível da barra (`aria-label` do `role="tablist"`). */
  label:    string;
}

/**
 * Barra de abas horizontal compartilhada (Admin Master e painéis tenant).
 *
 * Responsivo: abaixo de `sm` as abas mantêm a largura natural e a barra rola
 * horizontalmente — `flex-1` só entra a partir de `sm`, onde elas cabem sem
 * espremer o rótulo. Isso evita o colapso que acontecia com 4+ abas em 320px.
 */
export function TabBar<T extends string>({ items, active, onChange, label }: Props<T>) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className="scrollbar-hide -mx-1 flex snap-x snap-mandatory gap-1 overflow-x-auto rounded-2xl border border-white/[0.06] bg-white/[0.02] p-1.5 sm:mx-0"
    >
      {items.map(tab => {
        const Icon     = tab.icon;
        const isActive = active === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            // O rótulo curto e o longo coexistem no DOM (a troca é só por
            // breakpoint no CSS), então sem `aria-label` o nome acessível
            // viraria a concatenação dos dois. Fixamos no rótulo completo.
            aria-label={tab.label}
            onClick={() => onChange(tab.id)}
            className={`flex min-h-11 shrink-0 snap-start items-center justify-center gap-2 whitespace-nowrap rounded-xl px-3 py-2 text-sm font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40 sm:min-w-0 sm:flex-1 sm:shrink sm:gap-2.5 sm:px-4 ${
              isActive
                ? 'border border-orange-500/20 bg-orange-500/10 text-orange-400'
                : 'border border-transparent text-slate-500 hover:bg-white/[0.04] hover:text-slate-300'
            }`}
          >
            {Icon && (
              <Icon
                className={`h-4 w-4 shrink-0 ${isActive ? 'text-orange-400' : 'text-slate-600'}`}
                aria-hidden="true"
              />
            )}
            <span className="truncate sm:hidden" aria-hidden="true">
              {tab.shortLabel ?? tab.label}
            </span>
            <span className="hidden truncate sm:inline" aria-hidden="true">
              {tab.label}
            </span>
            {tab.badge !== undefined && tab.badge > 0 && (
              <span className="shrink-0 rounded-full bg-orange-500/15 px-1.5 text-xs text-orange-300">
                {tab.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
